import {Alert, Box, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {useNavigate} from "react-router-dom";
import Property from "crqlar-react-common/dist/components/Property";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {NewsletterAccountService} from "crqlar-react-common/dist/services/newsletter/newsletterAccount.service";
import {NewsletterAccountAccess} from "crqlar-react-common/dist/models/newsletter/newsletterAccountAccess";
import CopyAdornment from "crqlar-react-common/dist/components/CopyAdornment";

const NewsletterAccountDetails = () => {

    useTrackScreenVisit('system newsletter account details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()

    const newsletterAccountData = useEntity({
        get: async () => {
            return await NewsletterAccountService.getNewsletterAccountInfo()
        },
        update: NewsletterAccountService.updateNewsletterAccountInfo,
        create: NewsletterAccountService.updateNewsletterAccountInfo,
    })

    const access = newsletterAccountData.item
    const setAccess = newsletterAccountData.setItem


    //endregion Sate

    //region Handlers

    const updateField = (field: keyof NewsletterAccountAccess, value: any) => {
        setAccess({...access, [field]: value})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={t('Newsletter account')}
            data={newsletterAccountData}
            back={() => navigate('/system')}
        />
    )

    const titleSection = (
        <Typography variant={'body1'} color={'text.secondary'} textAlign={'start'}>
            {t('Last updated')}: {access.updateDate?.toLocaleString() || '-'}
        </Typography>
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Newsletter account name')}
                    tooltip={t('Internal label for identifying this newsletter account.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={access.newsletterAccountName || ''}
                        onChange={(e) => updateField('newsletterAccountName', e.target.value)}
                        fullWidth
                        required
                    />
                </Property>

                <Property
                    title={t('Newsletter account id')}
                    tooltip={t('External newsletter account identifier used by the integration.')}
                >
                    <TextField
                        id={'account-id'}
                        disabled={true}
                        size={'small'}
                        value={access.newsletterAccountId || ''}
                        onChange={(e) => updateField('newsletterAccountId', e.target.value)}
                        fullWidth
                        required
                        InputProps={{
                            endAdornment: <CopyAdornment value={access.newsletterAccountId?.toString() || ''} />,
                        }}
                    />
                </Property>

                <Property
                    title={t('Newsletter account subdomain')}
                    tooltip={t('Subdomain used by the newsletter provider for this account.')}
                >
                    <TextField
                        id={'account-subdomain'}
                        size={'small'}
                        value={access.newsletterAccountSubdomain || ''}
                        onChange={(e) => updateField('newsletterAccountSubdomain', e.target.value)}
                        fullWidth
                        required
                    />
                </Property>

                <Property
                    title={t('Login url')}
                    tooltip={t('Provider login URL staff can use to open the newsletter account.')}
                >
                    <TextField
                        id={'login-url'}
                        size={'small'}
                        value={access.newsletterAccountLoginUrl || ''}
                        onChange={(e) => updateField('newsletterAccountLoginUrl', e.target.value)}
                        fullWidth
                        required
                    />
                </Property>

                <Property
                    title={t('API key')}
                    tooltip={t('Credential used to connect to the newsletter provider. Leave empty to keep the current key.')}
                >
                    <Stack
                        gap={1}
                        width={'100%'}
                    >
                        <Alert
                            variant={'outlined'}
                            severity={'warning'}
                            sx={{width: '100%', py: 0, lineHeight: 1, alignItems: 'center'}}
                        >
                            {t('Leave this field empty to keep the current API key. Any value you enter here will override the current API key.')}
                        </Alert>

                        <TextField
                            id={'api-key'}
                            size={'small'}
                            value={access.apiKeyChange}
                            onChange={(e) => updateField('apiKeyChange', e.target.value)}
                            fullWidth
                            required
                        />
                    </Stack>

                </Property>
            </Stack>
        </Stack>
    )


    //endregion UI

    const dataUI = (
        <Stack
            sx={{
                width: '100%',
                maxWidth: '900px',
                mx: 'auto',
                mt: 3,
                gap: 6,
            }}
        >
            {titleSection}
            {basicInfoSection}
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}
            {dataUI}
        </Box>
    );
}

export default NewsletterAccountDetails;
